/**
 * shell/ClinicalRatingForm.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Lets a staff member (superadmin/admin/teacher) record an independent
 * "concern" / "no-concern" judgment against one specific QuickScreener
 * round or full SRT session — the ground truth ResearchCalibrationPage's
 * ROC/Youden's J reads, and the rater-pair source Cohen's κ reads when a
 * second person rates the same instance on the same day.
 *
 * Deliberately standalone rather than bolted onto wherever QuickScreener/
 * SRT results already render for staff — keeps this self-contained and
 * low-risk to drop in without needing to touch a large existing dashboard
 * file blind. Routed at /clinical-ratings/new.
 */

import { useState, useEffect } from "react";
import { useAuth } from "@/auth/AuthContext";
import { PANCHAKOSHA } from "@/constants/ncfFramework";
import { API, selectStyle } from "./ResearchShared";

const VERDICT_OPTIONS = [
  { value: '', label: 'Not rated' },
  { value: 'concern', label: 'Concern' },
  { value: 'no-concern', label: 'No concern' },
];

function initials(name = '') {
  return name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
}

function Avatar({ name, size = 24, tone = 'light' }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.42, fontWeight: 800,
      background: tone === 'navy' ? 'var(--navy)' : '#E5E7EB',
      color: tone === 'navy' ? '#fff' : '#374151',
    }}>
      {initials(name)}
    </span>
  );
}

function SidebarCard({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '16px 18px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--navy)', marginBottom: 11, letterSpacing: .5, textTransform: 'uppercase' }}>{title}</div>
      {children}
    </div>
  );
}

// The flow really is a fixed 3-step sequence, so a numbered tracker
// encodes real information here (and doubles as the fix for the page
// otherwise looking empty before a child is picked).
function StepTracker({ step }) {
  const steps = [
    { n: 1, label: 'Find child' },
    { n: 2, label: 'Pick instance' },
    { n: 3, label: 'Rate' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
      {steps.map((s, i) => (
        <div key={s.n} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', fontSize: 11, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              background: step > s.n ? 'var(--navy)' : '#fff',
              color: step > s.n ? '#fff' : step === s.n ? 'var(--navy)' : '#9CA3AF',
              border: step >= s.n ? '2px solid var(--navy)' : '2px solid #E5E7EB',
            }}>
              {step > s.n ? '✓' : s.n}
            </div>
            <span style={{ fontSize: 12, fontWeight: step === s.n ? 700 : 600, color: step >= s.n ? '#374151' : '#9CA3AF' }}>{s.label}</span>
          </div>
          {i < steps.length - 1 && <div style={{ width: 28, height: 1.5, background: step > s.n ? 'var(--navy)' : '#E5E7EB', margin: '0 10px' }} />}
        </div>
      ))}
    </div>
  );
}

export default function ClinicalRatingForm() {
  const { getAuthHeader } = useAuth();
  const [query,    setQuery]    = useState('');
  const [matches,  setMatches]  = useState([]);
  const [child,    setChild]    = useState(null);
  const [source,   setSource]   = useState(null);
  const [existing, setExisting] = useState([]);

  const [domainVerdict, setDomainVerdict] = useState({});
  const [overallVerdict, setOverallVerdict] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setMatches([]); return; }
    const t = setTimeout(() => {
      fetch(`${API}/clinical-ratings/lookup?q=${encodeURIComponent(query.trim())}`, { headers: getAuthHeader() })
        .then(r => r.json()).then(d => { if (d.success) setMatches(d.children); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!source) { setExisting([]); return; }
    fetch(`${API}/clinical-ratings/for/${source.sourceType}/${source.sourceId}`, { headers: getAuthHeader() })
      .then(r => r.json()).then(d => { if (d.success) setExisting(d.ratings); }).catch(() => {});
  }, [source]);

  function pickChild(c) {
    setChild(c); setSource(null); setSubmitted(false);
    setDomainVerdict({}); setOverallVerdict(''); setNotes('');
  }
  function pickSource(s) {
    setSource(s); setSubmitted(false);
    setDomainVerdict({}); setOverallVerdict(''); setNotes('');
  }

  async function submit() {
    if (!child || !source || !overallVerdict) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/clinical-ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({
          child: child._id, sourceType: source.sourceType, sourceId: source.sourceId,
          domainVerdict, overallVerdict, notes,
        }),
      });
      const d = await res.json();
      if (d.success) {
        setSubmitted(true);
        setExisting(prev => [...prev, d.rating]);
        setDomainVerdict({}); setOverallVerdict(''); setNotes('');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const step = !child ? 1 : !source ? 2 : 3;

  return (
    <div style={{ padding: '28px 26px 80px', maxWidth: 1020, margin: '0 auto', fontFamily: "'Inter',system-ui,sans-serif" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 900, color: 'var(--navy)' }}>Clinical Rating</div>
        <div style={{ fontSize: 12.5, color: 'var(--mist)', marginTop: 4, maxWidth: 620 }}>
          An independent verdict against one specific screening instance — kept separate from the tool's own flag, and from any other rater's verdict for the same instance.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* ── Main flow ──────────────────────────────────────────────── */}
        <div style={{ flex: '1 1 480px', minWidth: 320 }}>

          <StepTracker step={step} />

          <div style={{ marginBottom: 16 }}>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#9CA3AF' }}>🔍</span>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search child by name…"
                style={{ width: '100%', padding: '10px 12px 10px 34px', border: '1.5px solid #E5E7EB', borderRadius: 9, fontSize: 13, boxSizing: 'border-box' }} />
            </div>

            {query.trim().length > 0 && query.trim().length < 2 && (
              <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 7, padding: '0 2px' }}>Keep typing — at least 2 characters.</div>
            )}

            {query.trim().length >= 2 && matches.length === 0 && (
              <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8, padding: '9px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 9 }}>
                No child matches “{query.trim()}”. Check the spelling or try just a first name.
              </div>
            )}

            {matches.length > 0 && (
              <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, marginTop: 8, overflow: 'hidden' }}>
                {matches.map((c, i) => (
                  <button key={c._id} onClick={() => pickChild(c)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 12px',
                      background: child?._id === c._id ? '#FFF7ED' : '#fff', border: 'none',
                      borderBottom: i < matches.length - 1 ? '1px solid #F3F4F6' : 'none', cursor: 'pointer', fontSize: 13 }}>
                    <Avatar name={c.name} size={22} />
                    <span>{c.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {child && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Avatar name={child.name} size={30} tone="navy" />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: '#1a1a2e' }}>{child.name}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>Pick which screening instance you're rating</div>
                </div>
              </div>

              {child.sources.length === 0 ? (
                <div style={{ fontSize: 12, color: '#9CA3AF', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 9, padding: '10px 12px' }}>
                  No QuickScreener or SRT records found for this child yet.
                </div>
              ) : (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  {child.sources.map((s, i) => {
                    const isSelected = source && source.sourceType === s.sourceType && String(source.sourceId) === String(s.sourceId);
                    const isSRT = /srt/i.test(s.sourceType || '');
                    return (
                      <button key={`${s.sourceType}-${s.sourceId}`} onClick={() => pickSource(s)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px',
                          background: isSelected ? '#FFF7ED' : '#fff', border: 'none',
                          borderBottom: i < child.sources.length - 1 ? '1px solid #F3F4F6' : 'none', cursor: 'pointer', fontSize: 13 }}>
                        <span style={{ fontSize: 15 }}>{isSRT ? '📘' : '⚡'}</span>
                        <span style={{ flex: 1, color: '#374151' }}>{s.label}</span>
                        {isSelected && <span style={{ fontSize: 11.5, color: 'var(--navy)', fontWeight: 700 }}>Selected ✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {source && (
            <>
              {existing.length > 0 && (
                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#374151', marginBottom: 9 }}>Existing ratings for this instance</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {existing.map(r => {
                      const name = r.ratedBy?.name || 'Unknown';
                      const isConcern = r.overallVerdict === 'concern';
                      return (
                        <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <Avatar name={name} size={22} />
                          <span style={{ fontSize: 12, color: '#374151', flex: 1 }}>{name}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                            background: isConcern ? '#FEE2E2' : '#DCFCE7', color: isConcern ? '#B91C1C' : '#15803D' }}>
                            {isConcern ? 'Concern' : 'No concern'}
                          </span>
                          <span style={{ fontSize: 10, color: '#9CA3AF' }}>{new Date(r.createdAt).toLocaleDateString('en-IN')}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '16px 18px' }}>
                <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 12 }}>Your independent verdict</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                  {PANCHAKOSHA.map(k => (
                    <div key={k.id}>
                      <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 3 }}>{k.icon ? `${k.icon} ` : ''}{k.label}</label>
                      <select value={domainVerdict[k.id] || ''} onChange={e => setDomainVerdict(prev => ({ ...prev, [k.id]: e.target.value || null }))}
                        style={{ ...selectStyle, width: '100%' }}>
                        {VERDICT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>

                <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 3 }}>Overall (required)</label>
                <select value={overallVerdict} onChange={e => setOverallVerdict(e.target.value)} style={{ ...selectStyle, width: '100%', marginBottom: 12 }}>
                  <option value="">Select…</option>
                  <option value="concern">Concern</option>
                  <option value="no-concern">No concern</option>
                </select>

                <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 3 }}>Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  style={{ width: '100%', padding: '8px 11px', border: '1.5px solid #E5E7EB', borderRadius: 9, fontSize: 13, boxSizing: 'border-box', marginBottom: 14, resize: 'vertical' }} />

                <button onClick={submit} disabled={!overallVerdict || submitting}
                  style={{ padding: '10px 18px', background: !overallVerdict || submitting ? '#D1D5DB' : 'var(--navy)', color: '#fff',
                    border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: !overallVerdict || submitting ? 'default' : 'pointer' }}>
                  {submitting ? 'Submitting…' : 'Submit rating'}
                </button>
                {submitted && <span style={{ marginLeft: 10, fontSize: 12, color: '#16A34A', fontWeight: 600 }}>Saved.</span>}
              </div>
            </>
          )}
        </div>

        {/* ── Sidebar — fills the space, orients first-time raters ────── */}
        <div style={{ flex: '0 0 300px', minWidth: 260, display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 20 }}>
          <SidebarCard title="How this works">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              {[
                'Search for the child, then pick which QuickScreener round or SRT session you\'re rating.',
                'Give your own concern / no-concern call per domain, then one overall verdict — independent of what the tool flagged.',
                'Submit. If a second person rates the same instance the same day, that pair feeds inter-rater agreement (Cohen\'s κ).',
              ].map((text, i) => (
                <div key={i} style={{ display: 'flex', gap: 9 }}>
                  <div style={{ width: 17, height: 17, borderRadius: '50%', background: '#F3F4F6', color: '#6B7280',
                    fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                  <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.55 }}>{text}</div>
                </div>
              ))}
            </div>
          </SidebarCard>

          <SidebarCard title="Domains you'll rate">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PANCHAKOSHA.map(k => (
                <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: '#374151' }}>
                  <span style={{ fontSize: 14 }}>{k.icon || '•'}</span><span>{k.label}</span>
                </div>
              ))}
            </div>
          </SidebarCard>

          <SidebarCard title="Why it matters">
            <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>
              Your verdicts become the ground truth the calibration dashboard checks the tool's own flags against — that's how sensitivity, specificity, and rater agreement get measured.
            </div>
          </SidebarCard>
        </div>

      </div>
    </div>
  );
}